import React from 'react';

const PortfolioPerformance = ({ stats }) => {
  // Parse values and calculate dynamic bar widths
  const parsePercent = (val) => {
    if (!val) return 0;
    return parseFloat(val.replace('%', '').replace('+', ''));
  };
  const portfolioValue = parsePercent(stats.portfolio);
  const benchmarkValue = parsePercent(stats.benchmark);
  const maxValue = Math.max(portfolioValue, benchmarkValue, 1);
  const portfolioWidth = Math.min(portfolioValue, 100);
  const benchmarkWidth = Math.min(benchmarkValue, 100);

  return (
    <div className="bg-gray-50 rounded-xl p-2 sm:p-3 md:p-8 lg:p-10 shadow-inner w-full h-[22rem] max-w-full overflow-x-auto flex-1">
      <div className="h-full flex flex-col justify-between">
        {/* Header */}
        <div className="text-center animate-fade-in-down px-1 mb-1">
            <h2 className="text-base font-semibold text-[#113262] mb-1">Your Portfolio</h2>
            <p className="text-xs text-[#8A8A8A]">Current year-to-date performance</p>
        </div>

        {/* Performance Grid */}
        <div className="grid grid-cols-1 gap-2 md:gap-4 md:grid-cols-2 flex-1 relative md:items-stretch">
          {/* Your Portfolio */}
          <div className="bg-white p-2 sm:p-2 rounded-lg md:rounded-xl text-center relative border-2 border-transparent hover:border-[#1C52A0] transition-all duration-300 hover:-translate-y-1 hover:shadow animate-slide-in-up md:min-h-[14rem] md:flex md:flex-col md:justify-between" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-center mb-3">
              <div className="w-8 h-4 md:w-12 md:h-6 relative">
                <svg viewBox="0 0 70 30" className="w-full h-full">
                  <path 
                    className="fill-none stroke-[#22A06B] stroke-2 rounded-full animate-draw-line"
                    d="M5,25 Q15,20 25,15 T45,12 T65,8"
                  />
                </svg>
              </div>
            </div>
            <div className="text-[#22A06B] text-2xl lg:text-3xl font-bold mb-2 animate-count-up" style={{ animationDelay: '1.2s' }} >
              {stats.portfolio}
            </div>
            <div className="text-xs md:text-sm font-semibold text-[#113262] mb-0.5 md:mb-1">Your Portfolio</div>
            <div className="text-[10px] md:text-xs text-[#8A8A8A] leading-tight mb-1 md:mb-2">
              Current year-to-date performance
            </div>
            <div className="h-0.5 md:h-1 bg-gray-200 rounded-full overflow-hidden md:mt-auto">
              <div className="h-full bg-gradient-to-r from-[#22A06B] to-[#1F845A] rounded-full animate-fill-bar" style={{ width: `${portfolioWidth}%`, animationDelay: '1.5s' }}></div>
            </div>
          </div>

          {/* Nifty 50 TRI */}
          <div className="bg-white p-2 sm:p-2 rounded-lg md:rounded-xl text-center relative border-2 border-transparent hover:border-[#1C52A0] transition-all duration-300 hover:-translate-y-1 hover:shadow animate-slide-in-up md:min-h-[14rem] md:flex md:flex-col md:justify-between" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center justify-center mb-3">
              <div className="w-8 h-4 md:w-12 md:h-6 relative">
                <svg viewBox="0 0 70 30" className="w-full h-full">
                  <path 
                    className="fill-none stroke-[#1C52A0] stroke-2 rounded-full animate-draw-line"
                    d="M5,22 Q15,18 25,12 T45,8 T65,5"
                    style={{ animationDelay: '0.5s' }}
                  />
                </svg>
              </div>
            </div>
            <div className="text-2xl lg:text-3xl font-bold text-[#8A8A8A] mb-2 animate-count-up" style={{ animationDelay: '1.2s' }}>
              {stats.benchmark}
            </div>
            <div className="text-xs md:text-sm font-semibold text-[#113262] mb-0.5 md:mb-1">Nifty 50 TRI</div>
            <div className="text-[10px] md:text-xs text-[#8A8A8A] leading-tight mb-1 md:mb-2">
              Benchmark index performance
            </div>
            <div className="h-0.5 md:h-1 bg-gray-200 rounded-full overflow-hidden md:mt-auto">
              <div className="h-full bg-gradient-to-r from-[#8A8A8A] to-[#545454] rounded-full animate-fill-bar" style={{ width: `${benchmarkWidth}%`, animationDelay: '1.7s' }}></div>
            </div>
          </div>

          {/* VS Divider */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#1C52A0] text-white w-6 h-6 md:w-9 md:h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-[10px] md:text-xs sm:text-sm shadow z-10 animate-pulse-gentle hidden md:flex">
            VS
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes count-up {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes draw-line {
          to {
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes fill-bar {
          from {
            width: 0;
          }
        }
        
        @keyframes pulse-gentle {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.05);
          }
        }
        
        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out both;
        }
        
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out both;
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out both;
        }
        
        .animate-count-up {
          animation: count-up 1s ease-out both;
        }
        
        .animate-draw-line {
          stroke-dasharray: 200;
          stroke-dashoffset: 200;
          animation: draw-line 2s ease-in-out forwards;
        }
        
        .animate-fill-bar {
          animation: fill-bar 1.5s ease-out both;
        }
        
        .animate-pulse-gentle {
          animation: pulse-gentle 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default PortfolioPerformance;